/**
 * El envío dentro de la ficha de un pedido físico: a dónde va, cómo va y el
 * botón para marcarlo.
 *
 * No hay seguimiento automático (tampoco en Webflow): quien vende lleva el
 * paquete, escribe la paquetería y la guía y lo marca enviado. Eso le manda al
 * comprador el correo «va en camino» con el enlace de seguimiento, que el
 * servidor arma solo para las paqueterías conocidas.
 *
 * El formulario de la guía se abre aquí mismo y no en otra ventana: la ficha
 * ya es un diálogo, y uno encima de otro para dos campos sobraba.
 */
import { useState } from 'react'
import {
  IconAlertTriangle,
  IconArrowBackUp,
  IconPrinter,
  IconBuildingStore,
  IconCheck,
  IconCopy,
  IconCreditCard,
  IconDots,
  IconExternalLink,
  IconLoader2,
  IconMail,
  IconMapPin,
  IconPackage,
  IconPhone,
  IconReceiptRefund,
  IconTruckDelivery,
} from '@tabler/icons-react'
import { getPackingSlip, updateOrderFulfillment, type FulfillmentStatus, type OrderEvent, type OrderShipping } from '../../../lib/api'
import { ConfirmDialog } from '../ConfirmDialog'
import { formatMoney } from './offerShared'
import { translateServerError, useI18n } from '../../../lib/i18n'
import { useToast } from '../../Toast'

/**
 * Las paqueterías con enlace automático. Es la lista de `CARRIERS` en el
 * worker (services/shipping.ts): si se añade una allí, va aquí también.
 */
const CARRIERS = [
  { key: 'usps', name: 'USPS' },
  { key: 'ups', name: 'UPS' },
  { key: 'fedex', name: 'FedEx' },
  { key: 'dhl', name: 'DHL' },
] as const

const carrierName = (key: string | null) => CARRIERS.find((row) => row.key === key)?.name ?? key ?? ''

type StepKey = 'orders.shipping.step.preparing' | 'orders.shipping.step.shipped' | 'orders.shipping.step.ready'

/** A qué estados se puede volver desde cada uno, del más cercano al más lejano. */
const CORRECTIONS: Partial<Record<FulfillmentStatus, { to: FulfillmentStatus; label: StepKey }[]>> = {
  shipped: [{ to: 'unfulfilled', label: 'orders.shipping.step.preparing' }],
  ready_for_pickup: [{ to: 'unfulfilled', label: 'orders.shipping.step.preparing' }],
  delivered: [{ to: 'shipped', label: 'orders.shipping.step.shipped' }, { to: 'unfulfilled', label: 'orders.shipping.step.preparing' }],
  picked_up: [{ to: 'ready_for_pickup', label: 'orders.shipping.step.ready' }, { to: 'unfulfilled', label: 'orders.shipping.step.preparing' }],
}

/** El nombre del paso al que lleva un estado, para el historial y las correcciones. */
const STEP_OF: Record<string, string> = {
  unfulfilled: 'orders.shipping.step.preparing',
  shipped: 'orders.shipping.step.shipped',
  delivered: 'orders.shipping.step.delivered',
  ready_for_pickup: 'orders.shipping.step.ready',
  picked_up: 'orders.shipping.step.pickedUp',
}

export function OrderShippingPanel({ projectId, token, orderId, shipping, events = [], currency, onChange, onRefresh }: {
  projectId: string
  token: string
  orderId: string
  shipping: OrderShipping
  onChange: (shipping: OrderShipping) => void
  /** El historial del pedido (del más antiguo al más reciente). */
  events?: OrderEvent[]
  /** La moneda del cobro, para escribir los importes del historial. */
  currency: string
  /** Volver a pedir la ficha tras un cambio, para que el historial lo diga. */
  onRefresh?: () => void
}) {
  const { t } = useI18n()
  const { showToast } = useToast()
  const [form, setForm] = useState<{ carrier: string; other: string; number: string; url: string; notify: boolean } | null>(null)
  const [busy, setBusy] = useState<FulfillmentStatus | null>(null)

  const known = CARRIERS.some((row) => row.key === shipping.carrier)
  const openForm = () => setForm({
    carrier: shipping.carrier ? (known ? shipping.carrier : 'other') : 'usps',
    other: known ? '' : (shipping.carrier ?? ''),
    number: shipping.trackingNumber ?? '',
    url: known ? '' : (shipping.trackingUrl ?? ''),
    notify: true,
  })

  const apply = async (status: FulfillmentStatus, extra: { carrier?: string | null; trackingNumber?: string | null; trackingUrl?: string | null; notify?: boolean } = {}) => {
    setBusy(status)
    try {
      const saved = await updateOrderFulfillment(projectId, orderId, { status, ...extra }, token)
      onChange({ ...shipping, ...saved, status: saved.fulfillmentStatus })
      onRefresh?.()
      setForm(null)
      showToast(t(saved.notified ? 'orders.shipping.savedNotified' : 'orders.shipping.saved'))
    } catch (cause) {
      showToast(translateServerError(cause, t('error.unexpected')), 'error')
    } finally {
      setBusy(null)
    }
  }

  /*
   * La hoja de empaque: la que va dentro de la caja, no la etiqueta (ésa la
   * hace la paquetería). La ventana se abre ANTES de pedirla —si se abriera al
   * volver la respuesta, el navegador la tomaría por una ventana emergente y la
   * bloquearía— y se rellena al llegar; se imprime sola.
   */
  const [printing, setPrinting] = useState(false)
  const printSlip = async () => {
    const win = window.open('', '_blank')
    setPrinting(true)
    try {
      const { html } = await getPackingSlip(projectId, orderId, token)
      if (win) {
        win.document.open()
        win.document.write(html)
        win.document.close()
      }
    } catch (cause) {
      win?.close()
      showToast(translateServerError(cause, t('error.unexpected')), 'error')
    } finally {
      setPrinting(false)
    }
  }

  const copyAddress = () => {
    void navigator.clipboard?.writeText(shipping.formattedAddress)
      .then(() => showToast(t('orders.shipping.copied')))
      .catch(() => {})
  }

  const status = shipping.status
  /* Corregir el estado: escondido en «⋯» y con confirmación. Es para arreglar
     un error, no un paso del recorrido. */
  const [menuOpen, setMenuOpen] = useState(false)
  const [correcting, setCorrecting] = useState<{ to: FulfillmentStatus; label: StepKey } | null>(null)
  const corrections = CORRECTIONS[status] ?? []
  /* Si el comprador eligió recoger: el pedido no guarda el tipo de tarifa,
     pero una recogida llega sin dirección — Stripe sólo la pide para enviar. */
  const pickupOrder = !shipping.address?.line1
  /* Por qué camino va: recoger si ya se marcó listo o recogido, o si no hay a
     dónde mandarlo; si no, envío. */
  const pickupPath = status === 'ready_for_pickup' || status === 'picked_up' || (status === 'unfulfilled' && pickupOrder)
  const steps = pickupPath
    ? ['orders.shipping.step.paid', 'orders.shipping.step.preparing', 'orders.shipping.step.ready', 'orders.shipping.step.pickedUp']
    : ['orders.shipping.step.paid', 'orders.shipping.step.preparing', 'orders.shipping.step.shipped', 'orders.shipping.step.delivered']
  const currentStep = status === 'unfulfilled' ? 1 : status === 'shipped' || status === 'ready_for_pickup' ? 2 : 3
  const finished = status === 'delivered' || status === 'picked_up'
  const trackingLabel = [carrierName(shipping.carrier), shipping.trackingNumber].filter(Boolean).join(' · ')

  /* El albarán y las correcciones, junto a la acción del paso: son lo que se
     usa al preparar el paquete. Sueltos en su propia fila parecían otro paso. */
  const tools = (
    <>
      <button type="button" className="dash-secondary-btn" disabled={printing} onClick={() => { void printSlip() }}>
        {printing ? <IconLoader2 size={14} className="dash-spin" /> : <IconPrinter size={14} />}
        {t('orders.shipping.printSlip')}
      </button>
      {corrections.length ? (
        <div className="dash-ofs-more">
          <button
            type="button"
            className="dash-icon-btn"
            aria-label={t('orders.shipping.more')}
            data-tooltip={t('orders.shipping.more')}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <IconDots size={16} />
          </button>
          {menuOpen ? (
            <div className="dash-ofs-menu" role="menu" onMouseLeave={() => setMenuOpen(false)}>
              <span className="dash-ofs-menu-title">{t('orders.shipping.correctTitle')}</span>
              {corrections.map((option) => (
                <button key={option.to} type="button" role="menuitem" onClick={() => { setMenuOpen(false); setCorrecting(option) }}>
                  <IconArrowBackUp size={14} /> {t('orders.shipping.backTo', { status: t(option.label) })}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  )

  return (
    <div className="dash-ofs">
      {/* En qué punto va, de un vistazo: lo hecho, lo de ahora y lo que falta. */}
      <ol className="dash-ofs-steps" aria-label={t('orders.shipping.title')}>
        {steps.map((key, index) => (
          <li key={key} className={index < currentStep || (finished && index === currentStep) ? 'is-done' : index === currentStep ? 'is-current' : ''}>
            <span className="dash-ofs-dot" aria-hidden="true">{index < currentStep || (finished && index === currentStep) ? <IconCheck size={11} stroke={3} /> : null}</span>
            <span className="dash-ofs-step-label">{t(key)}</span>
          </li>
        ))}
      </ol>

      {/* Lo siguiente que hay que hacer, con su explicación en una línea y un
          solo botón grande. Lo demás, más pequeño, al pie. */}
      {form ? (
        <div className="dash-order-shipping-form">
          <div className="dash-ship-form-row">
            <label className="dash-svc-field">
              <span>{t('orders.shipping.carrier')}</span>
              <select value={form.carrier} onChange={(event) => setForm({ ...form, carrier: event.target.value })}>
                {CARRIERS.map((row) => <option key={row.key} value={row.key}>{row.name}</option>)}
                <option value="other">{t('orders.shipping.otherCarrier')}</option>
              </select>
            </label>
            {form.carrier === 'other' ? (
              <label className="dash-svc-field">
                <span>{t('orders.shipping.carrierName')}</span>
                <input value={form.other} maxLength={60} onChange={(event) => setForm({ ...form, other: event.target.value })} />
              </label>
            ) : null}
          </div>
          <label className="dash-svc-field">
            <span>{t('orders.shipping.number')}</span>
            <input value={form.number} maxLength={120} autoFocus onChange={(event) => setForm({ ...form, number: event.target.value })} />
          </label>
          {form.carrier === 'other' ? (
            <label className="dash-svc-field">
              <span className="dash-svc-label-row">{t('orders.shipping.url')}<em>{t('offers.new.optional')}</em></span>
              <input type="url" inputMode="url" placeholder="https://…" value={form.url} onChange={(event) => setForm({ ...form, url: event.target.value })} />
            </label>
          ) : null}
          <label className="dash-order-shipping-notify">
            <input type="checkbox" checked={form.notify} onChange={(event) => setForm({ ...form, notify: event.target.checked })} />
            <span>{t('orders.shipping.notify')}</span>
          </label>
          <div className="dash-ship-form-actions">
            <button type="button" className="dash-secondary-btn" disabled={busy !== null} onClick={() => setForm(null)}>{t('offers.new.cancel')}</button>
            <button
              type="button"
              className="dash-primary-btn"
              disabled={busy !== null}
              onClick={() => {
                const carrier = form.carrier === 'other' ? form.other.trim() || null : form.carrier
                void apply('shipped', {
                  carrier,
                  trackingNumber: form.number.trim() || null,
                  trackingUrl: form.carrier === 'other' ? form.url.trim() || null : null,
                  notify: form.notify,
                })
              }}
            >
              {busy === 'shipped' ? <IconLoader2 size={14} className="dash-spin" /> : <IconTruckDelivery size={14} />}
              {t(status === 'shipped' ? 'orders.shipping.saveTracking' : 'orders.shipping.markShipped')}
            </button>
          </div>
        </div>
      ) : finished ? null : (
        /* Entregado o recogido, nada: los cuatro pasos marcados ya lo dicen, y
           una tarjeta para avisar de que no hay nada que hacer era ruido. */
        <div className="dash-ofs-next">
          <p>{t(status === 'unfulfilled' ? (pickupOrder ? 'orders.shipping.hint.unfulfilledPickup' : 'orders.shipping.hint.unfulfilledShip') : `orders.shipping.hint.${status}`)}</p>
          {/* Una sola acción, la del camino que eligió el comprador: con las
              dos juntas había que leer la explicación para saber cuál tocaba. */}
          {status === 'unfulfilled' ? (
            <div className="dash-ofs-next-actions">
              <span className="dash-ofs-tools">{tools}</span>
              {pickupOrder ? (
                <button type="button" className="dash-primary-btn" disabled={busy !== null} onClick={() => { void apply('ready_for_pickup') }}>
                  {busy === 'ready_for_pickup' ? <IconLoader2 size={14} className="dash-spin" /> : <IconBuildingStore size={15} />}
                  {t('orders.shipping.markReady')}
                </button>
              ) : (
                <button type="button" className="dash-primary-btn" onClick={openForm}>
                  <IconTruckDelivery size={15} /> {t('orders.shipping.markShipped')}
                </button>
              )}
            </div>
          ) : status === 'shipped' ? (
            <div className="dash-ofs-next-actions">
              <span className="dash-ofs-tools">{tools}</span>
              <button type="button" className="dash-primary-btn" disabled={busy !== null} onClick={() => { void apply('delivered') }}>
                {busy === 'delivered' ? <IconLoader2 size={14} className="dash-spin" /> : <IconCheck size={15} />}
                {t('orders.shipping.markDelivered')}
              </button>
            </div>
          ) : status === 'ready_for_pickup' ? (
            <div className="dash-ofs-next-actions">
              <span className="dash-ofs-tools">{tools}</span>
              <button type="button" className="dash-primary-btn" disabled={busy !== null} onClick={() => { void apply('picked_up') }}>
                {busy === 'picked_up' ? <IconLoader2 size={14} className="dash-spin" /> : <IconCheck size={15} />}
                {t('orders.shipping.markPickedUp')}
              </button>
            </div>
          ) : null}
        </div>
      )}

      {finished ? <div className="dash-ofs-foot">{tools}</div> : null}

      {/* La dirección como en una etiqueta: renglones a la izquierda, con el
          teléfono y la forma de envío en la misma tarjeta. */}
      <div className="dash-ofs-card">
        <div className="dash-ofs-card-head">
          <span>
            {pickupOrder ? <IconBuildingStore size={14} aria-hidden="true" /> : <IconMapPin size={14} aria-hidden="true" />}
            {t(pickupOrder ? 'orders.shipping.pickupTitle' : 'orders.shipping.shipTo')}
          </span>
          {!pickupOrder && shipping.formattedAddress ? (
            <button type="button" className="dash-order-detail-offer-link" onClick={copyAddress}>
              <IconCopy size={12} /> {t('orders.shipping.copy')}
            </button>
          ) : null}
        </div>
        {shipping.formattedAddress ? <p className="dash-ofs-address">{shipping.formattedAddress}</p> : null}
        <ul className="dash-ofs-facts">
          {shipping.phone ? (
            <li><IconPhone size={13} aria-hidden="true" /><a href={`tel:${shipping.phone.replace(/\s+/g, '')}`}>{shipping.phone}</a></li>
          ) : null}
          {shipping.rateName ? (
            <li><IconTruckDelivery size={13} aria-hidden="true" /><span>{pickupOrder ? shipping.rateName : `${shipping.rateName} · ${shipping.amountFormatted}`}</span></li>
          ) : null}
        </ul>
      </div>

      {/* La guía, cuando ya salió: con su enlace a la página de la paquetería. */}
      {trackingLabel ? (
        <div className="dash-ofs-card dash-ofs-tracking">
          <div>
            <span className="dash-ofs-card-kicker">{t('orders.shipping.tracking')}</span>
            <strong>{trackingLabel}</strong>
          </div>
          <div className="dash-ofs-tracking-actions">
            {shipping.trackingUrl ? (
              <a className="dash-secondary-btn" href={shipping.trackingUrl} target="_blank" rel="noreferrer">
                {t('orders.shipping.track')} <IconExternalLink size={13} />
              </a>
            ) : null}
            {status === 'shipped' && !form ? (
              <button type="button" className="dash-order-detail-offer-link" onClick={openForm}>{t('orders.shipping.editTracking')}</button>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Lo que pasó con el pedido, lo más reciente arriba. */}
      {events.length ? (
        <div className="dash-ofs-history">
          <span className="dash-ofs-card-kicker">{t('orders.history.title')}</span>
          <ol>
            {[...events].reverse().map((event) => <HistoryItem key={event.id} event={event} currency={currency} />)}
          </ol>
        </div>
      ) : null}

      {correcting ? (
        <ConfirmDialog
          title={t('orders.shipping.correctConfirmTitle')}
          message={t('orders.shipping.correctConfirmBody', { status: t(correcting.label) })}
          confirmLabel={t('orders.shipping.backTo', { status: t(correcting.label) })}
          onCancel={() => setCorrecting(null)}
          onConfirm={async () => { await apply(correcting.to, { notify: false }); setCorrecting(null) }}
        />
      ) : null}
      {shipping.restockedAt ? <small className="dash-order-refunded-note">{t('orders.shipping.restocked')}</small> : null}
    </div>
  )
}

/** Una línea del historial: qué pasó, quién y cuándo. */
function HistoryItem({ event, currency }: { event: OrderEvent; currency: string }) {
  const { t, fmt } = useI18n()
  const meta = event.meta
  const amount = typeof meta.amount === 'number' ? formatMoney(meta.amount, typeof meta.currency === 'string' ? meta.currency : currency) : ''
  let Icon = IconCheck
  let title = ''
  let detail = ''
  if (event.kind === 'paid') {
    Icon = IconCreditCard
    title = t('orders.history.paid', { amount })
  } else if (event.kind === 'email_confirmation') {
    Icon = IconMail
    title = t('orders.history.emailConfirmation')
  } else if (event.kind === 'email_shipment') {
    Icon = IconMail
    title = t(meta.status === 'ready_for_pickup' ? 'orders.history.emailReady' : 'orders.history.emailShipped')
  } else if (event.kind === 'email_refund') {
    Icon = IconMail
    title = t('orders.history.emailRefund')
  } else if (event.kind === 'refunded') {
    Icon = IconReceiptRefund
    title = t(meta.full ? 'orders.history.refundedFull' : 'orders.history.refundedPartial', { amount })
  } else if (event.kind === 'restocked') {
    Icon = IconPackage
    title = t('orders.history.restocked')
  } else if (event.kind === 'oversold') {
    /* Stripe no aparta unidades mientras se paga: se vendió de más. Lo que
       falta va en el detalle, para escribirle a quien compró. */
    Icon = IconAlertTriangle
    title = t('orders.history.oversold')
    detail = Array.isArray(meta.items) ? meta.items.filter((item): item is string => typeof item === 'string').join(', ') : ''
  } else {
    const to = typeof meta.to === 'string' ? meta.to : ''
    if (meta.correction) {
      Icon = IconArrowBackUp
      title = t('orders.history.corrected', { status: t(STEP_OF[to] ?? 'orders.shipping.step.preparing') })
    } else if (to === 'shipped') {
      Icon = IconTruckDelivery
      title = t('orders.history.shipped')
      detail = [carrierName(typeof meta.carrier === 'string' ? meta.carrier : null), typeof meta.trackingNumber === 'string' ? meta.trackingNumber : ''].filter(Boolean).join(' · ')
    } else if (to === 'ready_for_pickup') {
      Icon = IconBuildingStore
      title = t('orders.history.ready')
    } else if (to === 'picked_up') {
      title = t('orders.history.pickedUp')
    } else if (to === 'delivered') {
      title = t('orders.history.delivered')
    } else {
      Icon = IconArrowBackUp
      title = t('orders.history.corrected', { status: t(STEP_OF[to] ?? 'orders.shipping.step.preparing') })
    }
  }
  const who = event.actorKind === 'stripe' ? 'Stripe'
    : event.actorKind === 'system' ? t('orders.history.auto')
      : event.actorName.trim() ? t(event.actorKind === 'team' ? 'orders.history.byTeam' : 'orders.history.byCustomer', { name: event.actorName.trim() }) : ''
  return (
    <li className={`dash-ofs-event is-${event.kind}${meta.correction ? ' is-correction' : ''}`}>
      <span className="dash-ofs-event-icon" aria-hidden="true"><Icon size={13} /></span>
      <span className="dash-ofs-event-copy">
        <strong>{title}</strong>
        {detail ? <span>{detail}</span> : null}
        <small>{[fmt.dateTime(event.at), who].filter(Boolean).join(' · ')}</small>
      </span>
    </li>
  )
}
